/**
 * Every choice turn in the diagnostic renders through the signed-in quiz's own
 * QuestionCard + SubmitButton (variant "onboarding"), so a change to the bank's
 * question UI lands here too. No correctness reveal: once locked, the turn shows
 * `children` (Pam's read + Continue) instead.
 */
import React, { useEffect, useRef, useState } from 'react';
import { QuestionCard } from '@/features/quiz/components/QuestionCard';
import { SubmitButton } from '@/features/quiz/components/SubmitButton';
import type { Question } from '@/types/adaptive';
import type { AdaptiveTurnQuestion } from '../adaptiveEngine';
import { PamNote } from './PamNote';

const LETTERS = ['A', 'B', 'C', 'D'] as const;

export interface DiagnosticChoice {
  id: string;
  text: string;
}

export interface DiagnosticQuestionProps {
  id: string;
  category: string;
  stem: string;
  /** At most four; rendered as A–D in order. */
  choices: DiagnosticChoice[];
  locked: boolean;
  submitting?: boolean;
  onSubmit: (choiceId: string) => void;
  children?: React.ReactNode;
}

const noop = () => undefined;

export const DiagnosticQuestion: React.FC<DiagnosticQuestionProps> = ({
  id,
  category,
  stem,
  choices,
  locked,
  submitting = false,
  onSubmit,
  children,
}) => {
  const [selected, setSelected] = useState('');
  // A long stem pushes Submit (and later Pam's read) below a phone's fold; bring
  // the next action on screen as soon as it is the thing to tap.
  const actionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected || locked) actionRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [selected, locked]);
  const text = (i: number) => choices[i]?.text ?? '';
  const question: Question = {
    id,
    category,
    question: stem,
    option_a: text(0),
    option_b: text(1),
    option_c: text(2),
    option_d: text(3),
  };

  const submit = () => {
    const choice = choices[LETTERS.indexOf(selected as (typeof LETTERS)[number])];
    if (choice) onSubmit(choice.id);
  };

  return (
    <div className="space-y-5" data-testid="diagnostic-question">
      <QuestionCard
        variant="onboarding"
        question={question}
        selectedAnswer={selected}
        onAnswerSelect={setSelected}
        showHint={false}
        onToggleHint={noop}
        showingFeedback={locked || submitting}
        confidence={null}
        onConfidenceChange={noop}
        flaggedForReview={false}
        onToggleFlagForReview={noop}
      />
      <div ref={actionRef} className="scroll-mb-4">
        {locked ? (
          children
        ) : (
          <SubmitButton
            selectedAnswer={selected}
            isSubmitting={submitting}
            onSubmit={submit}
            confidence={null}
            requireConfidence={false}
          />
        )}
      </div>
    </div>
  );
};

export const ContinueButton: React.FC<{ onClick: () => void; label?: string }> = ({
  onClick,
  label = 'Continue',
}) => (
  <button type="button" onClick={onClick} className="btn-primary w-full" data-testid="diagnostic-continue">
    {label}
  </button>
);

/** A bank question (turns 1, 2, 4): submit locks it, then Pam's read + Continue. */
export const AdaptiveQuestionBody: React.FC<{
  question: AdaptiveTurnQuestion;
  choiceId: string | null;
  /** Locks the card with no answer (turn 4's clock ran out). */
  timedOut?: boolean;
  onSubmit: (choiceId: string) => void;
  onContinue: () => void;
}> = ({ question, choiceId, timedOut = false, onSubmit, onContinue }) => {
  const read = question.pam ?? 'Noted. Every answer tells me where to take you next.';
  return (
    <DiagnosticQuestion
      id={`public-diag-${question.questionId}`}
      category={question.category}
      stem={question.stem}
      choices={question.options.map((o) => ({ id: o.choiceId, text: o.text }))}
      locked={choiceId !== null || timedOut}
      onSubmit={onSubmit}
    >
      <PamReadThenContinue
        read={choiceId === null && timedOut ? `Time ran out on this one, and that is fine. ${read}` : read}
        onContinue={onContinue}
      />
    </DiagnosticQuestion>
  );
};

/** After an answer: Pam's read of the pattern, then one way forward. */
export const PamReadThenContinue: React.FC<{ read: React.ReactNode; onContinue: () => void }> = ({
  read,
  onContinue,
}) => (
  <div className="space-y-4">
    <PamNote label="Pam's read" testId="pam-read">
      {read}
    </PamNote>
    <ContinueButton onClick={onContinue} />
  </div>
);
